// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { FormFieldIcon } from "@insite/mobius/FormField";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Globe from "@insite/mobius/Icons/Globe";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { ChevronDownOffset } from "@store/Library/Components/Toolkit/Icons/ChevronDown";
import { SelectInlineStyles } from "@store/Library/Components/Toolkit/SelectInline";
import { menuSecondaryNavigationItemStyles } from "@store/Themes/common/mixins/menu";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import { useCountriesWebsites } from "@store/Framework/Store/Data/WebsiteCountries/WebsiteCountriesHooks";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { focusStyles, iconScalableStyles } from "@toolkit/Themes/common/mixins";
import React, { FC } from "react";
import { css } from "styled-components";

const iconSize = pxToRem(18);
const iconOffset = pxToRem(0);

const enum fields {
    showIcon = "showIcon",
}

export interface WebsiteCountriesMenuStyles {
    countryMenuWrapper?: InjectableCss;
    websiteCountriesIcon?: IconPresentationProps;
    countryMenuSelect?: SelectPresentationProps;
    selectInline?: SelectInlineStyles;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
}

export const websiteCountriesMenuStyles: WebsiteCountriesMenuStyles = {
    countryMenuWrapper: {
        css: css`
            ${menuSecondaryNavigationItemStyles()}
            display: flex;
            align-items: center;

            &:hover {
                color: ${getColor("menu.secondaryNavigation.hover")};
                text-decoration: underline;
                transition: ${transitionHoverStyles()};
            }
        `,
    },
    websiteCountriesIcon: {
        src: Globe,
        color: "inherit",
        css: css`
            ${iconScalableStyles(iconSize, false)}
            margin-right: ${x => x.theme.design.menu.secondaryNavigation.iconSpacing};
            position: absolute;
        `,
    },
    selectInline: {
        select: {
            cssOverrides: {
                inputSelect: css`
                    font-weight: inherit;
                    line-height: inherit;
                    cursor: pointer;

                    &:hover {
                        text-decoration: underline;
                    }
                `,
            },
        },
    },
    countryMenuSelect: {
        labelProps: {
            css: css`
                ${x => x.theme.formField.defaultProps?.labelProps?.css}
                padding: 0;
                margin-right: 0.4em;
                font-size: inherit;
            `,
        },
        cssOverrides: {
            formField: css`
                width: auto;
            `,
            formInputWrapper: css`
                display: flex;

                ${FormFieldIcon} {
                    ${iconScalableStyles("1em", ChevronDownOffset)}
                    position: absolute;
                    top: 50%;
                    transform: translateY(-50%);
                    color: inherit;
                }
            `,
            inputSelect: css`
                ${focusStyles()}
                display: block;
                width: auto;
                height: auto;
                background-color: transparent;
                color: inherit;
                font-size: inherit;

                &:not(#important) {
                    border: 0;
                    border-radius: 0;
                    padding: 0 1.2em 0 0;
                }

                ${({ theme }) => {
                    const iconSpacing = theme.design.menu.secondaryNavigation.iconSpacing;
                    return `padding-left: calc(${iconSize} + ${iconSpacing} - (${iconOffset} * 2)) !important;`;
                }}
            `,
        },
    },
    centeringWrapper: {
        css: css`
            width: 50%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showIcon]: boolean;
    };
}

type Props = OwnProps;

export const WebsiteCountriesMenu: FC<Props> = ({ fields }) => {
    const { countriesWebsite, currentCountryWebsite } = useCountriesWebsites();

    const handleWebsiteCountryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedName = event.target.value;
        const selectedCountry = countriesWebsite.find(c => c.name === selectedName);
        if (selectedCountry && selectedCountry.url) {
            let finalUrl = selectedCountry.url;

            if (!finalUrl.startsWith("http://") && !finalUrl.startsWith("https://")) {
                finalUrl = `https://${finalUrl}`;
            }
            window.location.href = finalUrl;
        }
    };

    const styles = websiteCountriesMenuStyles;

    if (!countriesWebsite || countriesWebsite.length <= 1) {
        return (
            <StyledWrapper {...styles.countryMenuWrapper}>
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.countryMenuWrapper}>
            {fields.showIcon && <Icon {...styles.websiteCountriesIcon} />}
            <Select {...styles.countryMenuSelect} onChange={handleWebsiteCountryChange} value={currentCountryWebsite}>
                {countriesWebsite.map(c => (
                    <option value={c.name} key={c.id}>
                        {c.name}
                    </option>
                ))}
            </Select>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: WebsiteCountriesMenu,
    definition: {
        displayName: "Website Countries Menu",
        icon: "language",
        fieldDefinitions: [
            {
                name: fields.showIcon,
                displayName: "Show Icon",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
